import api from './client';

// Age Verification API (registration endpoints - no auth required)
export const ageVerificationAPI = {
  // Register with DOB verification
  registerWithDob: (data: {
    email: string;
    username: string;
    password: string;
    name: string;
    dateOfBirth: string;
    identity?: string;
    identityConsentGranted?: boolean;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmContent?: string;
    tosAccepted: boolean;
    emailNotifications?: boolean;
    // 2026-08-15 Onboarding red-team fix follow-up: set true for the Phone
    // tier's DOB collection step -- that tier's Social Access level is
    // permanent, not a temporary DOB-verification grace period pending
    // biometric upgrade like the DOB tier itself.
    skipDobGracePeriod?: boolean;
    /** 0zzz-tzu review B: the token of this browser's last Didit start; its name hold does not block, and is released with, this registration. */
    previousRegistrationToken?: string;
  }) => api.post('/age-verification/register/dob', {
    ...data,
    dateOfBirth: data.dateOfBirth, // Already camelCase, backend expects this
  }),

  // Didit verification endpoints
  // Check if Didit biometric verification is available
  checkDiditStatus: () => api.get('/age-verification/didit/status'),

  // Create a Didit session for registration (stores pending registration data)
  createRegistrationDiditSession: (data: {
    email: string;
    username: string;
    password: string;
    name: string;
    identity?: string;
    identityConsentGranted?: boolean;
    tosAccepted: boolean;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmContent?: string;
    emailNotifications?: boolean;
    /** 0zzz-tzu Q1: the token of this browser's previous start; proves "same person" so the earlier name hold is replaced. */
    previousRegistrationToken?: string;
  }) => api.post('/age-verification/register/didit/session', data),

  // Complete registration after Didit verification
  completeRegistrationWithDidit: (registrationToken: string, sessionId: string) =>
    api.post('/age-verification/register/didit/complete', { registrationToken, sessionId }),

  // Get Didit session result (for polling)
  getDiditResult: (sessionId: string) =>
    api.get(`/age-verification/didit/result/${sessionId}`),

  // Create a Didit session for an existing authenticated user (upgrade flow)
  createDiditSession: (options?: { successRedirect?: string; errorRedirect?: string }) =>
    api.post<{ sessionId: string; sessionUrl: string }>('/age-verification/didit/session', options || {}),
};

// Auth API
export const authAPI = {
  // previousRegistrationToken (0zzz-tzu review B) goes in a header, like checkUsername: it is a secret and the body schema is fixed.
  register: (email: string, username: string, password: string, name: string, dateOfBirth: string, tosAccepted: boolean, previousRegistrationToken?: string) => {
    const body = { email, username, password, name, date_of_birth: dateOfBirth, tos_accepted: tosAccepted };
    return previousRegistrationToken
      ? api.post('/auth/register', body, { headers: { 'X-Previous-Registration-Token': previousRegistrationToken } })
      : api.post('/auth/register', body);
  },

  // previousRegistrationToken (0zzz-tzu Q5) goes in a header, never the URL: it is a secret and this call fires as the person types.
  checkUsername: (username: string, previousRegistrationToken?: string) =>
    api.get('/auth/check-username', {
      params: { username },
      ...(previousRegistrationToken ? { headers: { 'X-Previous-Registration-Token': previousRegistrationToken } } : {}),
    }),

  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),

  logout: () => api.post('/auth/logout'),

  getProfile: () => api.get('/auth/profile'),

  updateProfile: (updates: { name?: string; username?: string; loginNotificationsEnabled?: boolean }) =>
    api.put('/auth/profile', updates),

  // Age verification (for existing users)
  verifyAge: (dateOfBirth: string) =>
    api.post('/auth/verify-age', { dateOfBirth }),

  // Email verification
  resendVerificationEmail: () => api.post('/auth/resend-verification'),

  verifyEmail: (token: string) => api.get(`/auth/verify-email/${token}`),

  getVerificationStatus: () => api.get('/auth/verification-status'),

  // Password reset
  forgotPassword: (email: string) => api.post('/auth/forgot-password', { email }),

  validateResetToken: (token: string) => api.get(`/auth/validate-reset-token/${token}`),

  resetPassword: (token: string, newPassword: string) =>
    api.post('/auth/reset-password', { token, newPassword }),

  // Account deletion
  requestAccountDeletion: () => api.post('/auth/request-deletion'),

  getDeletionStatus: () => api.get('/auth/deletion-status'),

  // CEO decision 2026-09-24: cancel-deletion now requires the current
  // password (verified server-side the same way login does).
  cancelAccountDeletion: (password: string) => api.post('/auth/cancel-deletion', { password }),

  // Terms of Service
  getTosStatus: () => api.get('/auth/tos-status'),

  acceptTos: () => api.post('/auth/accept-tos'),

  // Phase 3.1: Invitation acceptance
  checkInvitation: (token: string) =>
    api.get('/auth/check-invitation', { params: { token } }),

  acceptInvitation: (token: string) =>
    api.post('/auth/accept-invitation', { token }),

  uploadAvatar: (file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return api.post('/auth/avatar', formData);
  },

  deleteAvatar: () => api.delete('/auth/avatar'),

  // Fast user switch (Spec 079)
  linkAccount: (email: string, password: string) =>
    api.post('/auth/link-account', { email, password }),

  // Spec 193: Role-fork onboarding identity step
  // Spec 289: `role` widened to also accept 'exploring' — the Rhythm screen
  // is universal across all 4 identity options, but 'exploring' is not a
  // real users.identity_role value (DB CHECK only allows
  // dominant/submissive/switch); guidedOnboardingService.saveIdentity()
  // accepts 'exploring' as valid input and simply skips that column's
  // write for it. offerTags/packCap added for the Dominant-only
  // Personalize screen.
  saveOnboardingIdentity: (data: {
    role: 'dominant' | 'submissive' | 'switch' | 'exploring';
    displayName?: string;
    rhythm?: 'daily' | 'weekly' | 'flexible';
    limits?: string[];
    offerTags?: string[];
    packCap?: 'single' | 'small' | 'wide';
  }) => api.post('/onboarding/guided/identity', data),

  // Spec 289: Didit-resume mechanism — GET /api/users/identity (Spec 128,
  // already-authenticated, already exists). Used by
  // DiditRegistrationCallbackPage (resume-readiness check before navigating)
  // and RegisterPage (local `identity` state hydration on resume) so
  // neither has to re-collect what Didit already persisted server-side.
  getIdentity: () =>
    api.get<{
      identity: 'dominant' | 'submissive' | 'switch' | 'exploring';
      source: string;
      personalization: {
        defaultDashboardTab: string;
        emptyStateMessaging: string;
        discoveryDefaults: string;
      };
    }>('/users/identity'),
};

// Verification API — phone OTP + service availability
export const verificationAPI = {
  // Service availability (works with or without auth)
  getStatus: () =>
    api.get<{
      services: { didit: boolean; twilio: boolean };
      user: { level: string; emailVerified: boolean; phoneVerified: boolean; biometricVerified: boolean } | null;
    }>('/verification/status'),

  // Phone OTP
  sendPhoneOTP: (phoneNumber: string, smsConsentGiven: boolean = true) =>
    api.post('/verification/phone/send', { phoneNumber, smsConsentGiven }),

  confirmPhoneOTP: (code: string) =>
    api.post('/verification/phone/verify', { code }),

  getPhoneStatus: () =>
    api.get<{ phoneVerified: boolean }>('/verification/phone/status'),
};

// Sessions API - Session management
export const sessionsAPI = {
  // Get all active sessions for current user
  getSessions: () =>
    api.get<{
      sessions: Array<{
        id: string;
        deviceType: 'mobile' | 'tablet' | 'desktop' | 'unknown';
        deviceName: string;
        browser: string | null;
        os: string | null;
        city: string | null;
        region: string | null;
        country: string | null;
        appVersion: string | null;
        createdAt: string;
        lastActiveAt: string;
        isCurrent: boolean;
      }>;
      total: number;
    }>('/sessions'),

  // Get session count
  getSessionCount: () =>
    api.get<{ count: number }>('/sessions/count'),

  // Revoke a specific session (remote logout)
  revokeSession: (sessionId: string) =>
    api.delete(`/sessions/${sessionId}`),

  // Revoke all sessions except current
  revokeAllOtherSessions: () =>
    api.delete('/sessions', { data: { include_current: false } }),

  // Revoke all sessions including current
  revokeAllSessions: () =>
    api.delete('/sessions', { data: { include_current: true } }),
};
